import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import App from './App';
import { AppLayout } from './components/AppLayout';
import DashboardPage from "@/pages/DashboardPage";
import JobsPage from "@/pages/JobsPage";
import JobDetailPage from "@/pages/JobDetailPage";
import CandidatesPage from "@/pages/CandidatesPage";
import InterviewsPage from "@/pages/InterviewsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import SettingsPage from "@/pages/SettingsPage";
import AuditLogPage from "@/pages/AuditLogPage";
import EmailHistoryPage from "@/pages/EmailHistoryPage";
import InterviewPage from "@/components/InterviewPage";
import SharedResultsPage from "@/pages/SharedResultsPage";
import PublicTestPage from "@/pages/PublicTestPage";
import LoginPage from "@/pages/LoginPage";
import UsersPage from "@/pages/UsersPage";
import APISettingsPage from "@/pages/APISettingsPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import PreScreeningPage from "@/pages/PreScreeningPage";
import DeveloperPortalPage from "@/pages/DeveloperPortalPage";
import EmbedPage from "@/pages/EmbedPage";
import ChangelogPage from "@/pages/ChangelogPage";
import AssessmentLibraryPage from "@/pages/AssessmentLibraryPage";
import ScoutPage from "@/pages/ScoutPage";
import ScoutReportPage from "@/pages/ScoutReportPage";
import AssessmentTestPage from "@/pages/AssessmentTestPage";
import AssessmentTakePage from "@/pages/AssessmentTakePage";
import AssessmentReportPage, { SharedAssessmentReportPage } from "@/pages/AssessmentReportPage";
import { RegionProvider, useRegion } from "@/context/RegionContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import RegionSelector from "@/components/RegionSelector";
import { SharedMIReportPage, SharedJDAnalysisPage, SharedDiscoveryPage, SharedSourcingPage, SharedCombinedPage } from "@/pages/SharedAnalysisPages";
import { Loader2 } from 'lucide-react';

const AuthGate = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0f172a' }}>
      <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
    </div>
  );

  if (!user) return <Navigate to="/login" replace />;

  return children;
};

const RegionGate = ({ children }) => {
  const { region } = useRegion();
  if (!region) return <RegionSelector />;
  return children;
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <RegionProvider>
          <Routes>
            {/* Public routes — no auth, no region needed */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/interview/:token" element={<InterviewPage />} />
            <Route path="/shared/:shareToken" element={<SharedResultsPage />} />
            <Route path="/shared/mi/:shareToken" element={<SharedMIReportPage />} />
            <Route path="/shared/jd/:shareToken" element={<SharedJDAnalysisPage />} />
            <Route path="/shared/discovery/:shareToken" element={<SharedDiscoveryPage />} />
            <Route path="/shared/sourcing/:shareToken" element={<SharedSourcingPage />} />
            <Route path="/shared/complete/:shareToken" element={<SharedCombinedPage />} />
            <Route path="/public-test/:token" element={<PublicTestPage />} />
            <Route path="/set-password/:token" element={<SetPasswordPage />} />
            <Route path="/developers" element={<DeveloperPortalPage />} />
            <Route path="/developers/changelog" element={<ChangelogPage />} />
            <Route path="/embed/:token" element={<EmbedPage />} />
            <Route path="/assessment/:token" element={<AssessmentTakePage />} />
            <Route path="/assessment-report/:shareToken" element={<SharedAssessmentReportPage />} />

            {/* Protected app routes — auth gate then region gate */}
            <Route element={
              <AuthGate>
                <RegionGate>
                  <AppLayout />
                </RegionGate>
              </AuthGate>
            }>
              <Route index element={<DashboardPage />} />
              <Route path="jobs" element={<JobsPage />} />
              <Route path="jobs/:jobId" element={<JobDetailPage />} />
              <Route path="candidates" element={<CandidatesPage />} />
              <Route path="interviews" element={<InterviewsPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="emails" element={<EmailHistoryPage />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="integrations" element={<APISettingsPage />} />
              <Route path="pre-screening" element={<PreScreeningPage />} />
              <Route path="scout" element={<ScoutPage />} />
              <Route path="scout/:scanId" element={<ScoutReportPage />} />
              <Route path="assessments" element={<AssessmentLibraryPage />} />
              <Route path="assessments/report/:attemptId" element={<AssessmentReportPage />} />
              <Route path="assessments/:testId" element={<AssessmentTestPage />} />
            </Route>
          </Routes>
        </RegionProvider>
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
